import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle, BellRing, Loader2, Send, ChevronRight, ShieldAlert, Clock,
} from 'lucide-react';

const DOC_ICON = {
  kbis: ShieldAlert,
  urssaf: ShieldAlert,
  fiscale: ShieldAlert,
  identite: ShieldAlert,
};

const formatDays = (n) => {
  if (n == null) return '';
  if (n < 0) return `Expiré depuis ${Math.abs(n)} j`;
  if (n === 0) return "Expire aujourd'hui";
  if (n === 1) return 'Expire demain';
  return `Expire dans ${n} j`;
};

/**
 * Widget showing every subcontractor with an expiring or expired legal document.
 * Used on the Dashboard. Includes a "Send reminders" button (admin trigger).
 */
export default function DocumentAlertsWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/sous-traitants/documents/alerts');
      setData(r.data);
    } catch (_e) {
      setData({ alerts: [], expired_count: 0, warning_count: 0 });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendReminders = async () => {
    if (!window.confirm("Envoyer un email de rappel aux sous-traitants concernés ? (cooldown 7 jours par sous-traitant)")) return;
    setSending(true);
    try {
      const r = await api.post('/sous-traitants/documents/send-reminders');
      if (r.data.sent > 0) {
        toast.success(`${r.data.sent} email(s) de rappel envoyé(s)`);
      } else if (r.data.skipped > 0) {
        toast.info(`Tous les sous-traitants ont déjà été notifiés récemment (${r.data.skipped} ignoré(s))`);
      } else {
        toast.info('Aucun rappel à envoyer');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi des rappels");
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data || data.alerts.length === 0) {
    return null;  // Hide widget when no alerts
  }

  return (
    <div className="bg-gradient-to-br from-amber-50 to-red-50 rounded-xl border border-amber-200 overflow-hidden" data-testid="document-alerts-widget">
      <div className="p-5 border-b border-amber-200/60 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <BellRing className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Documents légaux à renouveler</h3>
            <p className="text-xs text-slate-600 mt-0.5">
              {data.expired_count > 0 && <span className="text-red-700 font-semibold">{data.expired_count} expiré(s)</span>}
              {data.expired_count > 0 && data.warning_count > 0 && <span className="text-slate-400 mx-1">·</span>}
              {data.warning_count > 0 && <span className="text-amber-700 font-semibold">{data.warning_count} à renouveler (&lt; {data.warning_threshold_days}j)</span>}
            </p>
          </div>
        </div>
        <button
          onClick={sendReminders}
          disabled={sending}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50"
          data-testid="send-doc-reminders-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? 'Envoi…' : 'Envoyer les rappels'}
        </button>
      </div>

      <div className="divide-y divide-amber-100/60 max-h-80 overflow-y-auto">
        {data.alerts.slice(0, 8).map((a, idx) => {
          const Icon = DOC_ICON[a.doc_type] || AlertTriangle;
          const isExpired = a.status === 'expired';
          return (
            <button
              key={`${a.sous_traitant_id}-${a.doc_type}-${idx}`}
              onClick={() => navigate(`/sous-traitants/${a.sous_traitant_id}`)}
              className="w-full p-4 flex items-center gap-3 hover:bg-white/60 text-left transition-colors"
              data-testid={`alert-row-${a.sous_traitant_id}-${a.doc_type}`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                isExpired ? 'bg-red-100' : 'bg-amber-100'
              }`}>
                <Icon className={`w-4 h-4 ${isExpired ? 'text-red-700' : 'text-amber-700'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900 text-sm truncate">{a.sous_traitant_nom}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    isExpired ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {isExpired ? 'Expiré' : 'À renouveler'}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-0.5 truncate">
                  {a.doc_label}
                  <span className="text-slate-400 mx-1.5">·</span>
                  <span className="inline-flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {formatDays(a.days_left)}
                  </span>
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
            </button>
          );
        })}
        {data.alerts.length > 8 && (
          <div className="p-3 text-center text-xs text-slate-500">
            + {data.alerts.length - 8} autre(s) sous-traitant(s) concerné(s)
          </div>
        )}
      </div>
    </div>
  );
}
